import { BlockBlobClient } from '@azure/storage-blob';
/**
 * Which storage service a pre-signed upload URL addresses.
 *
 * Decided from the signature query parameters rather than the hostname: a GCS
 * v4 signed URL always carries `X-Goog-Signature`, and matching on that works
 * for custom domains and for the Azurite emulator the dev setup uses, where the
 * host tells you nothing.
 *
 * Anything without a Google signature is treated as Azure, so every URL the
 * deployed estate issues today keeps taking exactly the path it takes today.
 */
export const signedUrlProvider = (uploadUrl) => {
    let parameters;
    try {
        parameters = new URL(uploadUrl).searchParams;
    }
    catch {
        return 'azure';
    }
    return parameters.has('X-Goog-Signature') || parameters.has('x-goog-signature') ? 'gcs' : 'azure';
};
/**
 * PUT a body to a URL that already authorises the write, reporting progress.
 *
 * XMLHttpRequest rather than fetch: fetch still has no upload progress event in
 * any shipping browser, and the callers all drive a progress bar.
 */
const putToSignedUrl = (uploadUrl, body, { onProgress, abortSignal }) => new Promise((resolve, reject) => {
    if (abortSignal?.aborted) {
        reject(new DOMException('Upload aborted', 'AbortError'));
        return;
    }
    const request = new XMLHttpRequest();
    request.open('PUT', uploadUrl, true);
    // Only set what the signature allows. A GCS v4 signature covers the headers
    // named in X-Goog-SignedHeaders; core-api signs none beyond the defaults, so
    // the content type is sent as a hint and never as a signed header.
    if (body.type) {
        request.setRequestHeader('Content-Type', body.type);
    }
    const onAbort = () => request.abort();
    abortSignal?.addEventListener('abort', onAbort, { once: true });
    const finish = () => abortSignal?.removeEventListener('abort', onAbort);
    request.upload.onprogress = (event) => {
        onProgress?.({ loadedBytes: event.loaded });
    };
    request.onload = () => {
        finish();
        if (request.status >= 200 && request.status < 300) {
            resolve();
            return;
        }
        reject(new Error(`Upload failed with status ${request.status}: ${request.statusText}`));
    };
    request.onerror = () => {
        finish();
        reject(new Error('Upload failed: the request could not reach storage'));
    };
    request.onabort = () => {
        finish();
        reject(new DOMException('Upload aborted', 'AbortError'));
    };
    request.send(body);
});
/**
 * Upload a file to a pre-signed URL, whichever cloud issued it.
 *
 * On Azure this is the block-blob upload the SDK has always done, unchanged —
 * including block size and parallelism. On GCS it is a single PUT: a v4 signed
 * URL authorises one request, and a resumable session needs its own separately
 * signed start, which core-api does not issue. So `blockSize` and `concurrency`
 * have no effect there, and a very large file uploads as one request rather
 * than in restartable parts.
 */
export const uploadToSignedUrl = async (uploadUrl, body, options = {}) => {
    if (signedUrlProvider(uploadUrl) === 'gcs') {
        await putToSignedUrl(uploadUrl, body, options);
        return;
    }
    await new BlockBlobClient(uploadUrl).uploadData(body, {
        onProgress: options.onProgress,
        abortSignal: options.abortSignal,
        blockSize: options.blockSize,
        concurrency: options.concurrency,
        // uploadData re-wraps the Blob internally, so its `type` never reaches
        // storage on its own — the content type has to be forwarded explicitly.
        ...(body.type ? { blobHTTPHeaders: { blobContentType: body.type } } : {}),
    });
};
